import { IArticleResponse } from '../../articles/models/articles.models';
import { ImportedArticleType } from '../../articles/models/drawing.models';
import { IClientSnapshot } from '../../customers/models/client-snapshot.models';
import { IAddressSnapshot } from '../../customers/models/customer-address.models';
import { Currency, VatExemptionStatus } from '../../customers/models/customer-billing.models';
import { ICustomerContact } from '../../customers/models/customer-contact.models';
import { IDocument } from '../../customers/models/customer-document.models';
import { IPaymentCondition } from '../../payment-conditions/models/payment-condition.models';
import { DeliveryTerm, IShippingMethod } from '../../shipping-methods/models/shipping-method.models';

export interface IBulkUpdateCustomerOrderRequestDto {
    id: string;
    version: string;
}

export interface IBulkUpdateCustomerOrderPositionsRequestDto {
    positions: IBulkUpdateCustomerOrderRequestDto[];
    targetDeliveryDate?: string | null;
    isReadyForDelivery?: boolean;
}

export interface IBulkUpdateConfig {
    deliveryDate: Date;
    applyToPosition: boolean;
}

export interface ICustomerOrder {
    customerId: string;
    customerContactId?: string | null;
    projectReferenceNumber?: string | null;
    externalReferenceNo?: string | null;
    source?: string | null;
    stateFlags?: OrderStatus[] | null;
    salesRepresentative?: string | null;
    priority: Priority;
    personInChargeId: string;
    targetDeliveryDate: string;
    completeReadyForDeliveryOn?: string | null;
    paymentConditionId: string;
    vatExemptionStatus: VatExemptionStatus;
    discount: number;
    surcharge: number;
    deliveryTerms: DeliveryTerm;
    shippingMethodId: string;
    note?: string | null;
    id: string;
    customerOrderNumber?: string | null;
    version?: string | null;
    status: OrderStatus;
    registrationDate?: string | null;
    currency: Currency;
    legalAddress: IAddressSnapshot;
    differingDeliveryAddress: IAddressSnapshot;
    differingInvoiceAddress: IAddressSnapshot;
    details: OrderDetails;
    positions: ICustomerOrderPositionResponseDto[];
    documents: IDocument;
    newDocuments?: string[] | null;
    customerContact: ICustomerContact;
    paymentCondition: IPaymentCondition;
    shippingMethod: IShippingMethod;
    buyer?: IClientSnapshot | null;
    seller?: IClientSnapshot | null;
    partialDeliveryAllowed: boolean;
    overDeliveryAllowed: boolean;
    hasDifferingTargetDeliveryDates?: boolean;
}

export interface OrderDetails {
    netTotalPrice: number;
    grossTotalPrice: number;
}

export enum OrderStatus {
    Draft = 'Draft',
    Valid = 'Valid',
    Confirmed = 'Confirmed',
    Planned = 'Planned',
    InProduction = 'InProduction',
    ReadyForDelivery = 'ReadyForDelivery',
    Delivered = 'Delivered',
    Invoiced = 'Invoiced',
    Blocked = 'Blocked',
    ActionRequired = 'ActionRequired',
    Cancelled = 'Cancelled',
}

export enum AllowReadyForDelivery {
    Confirmed = 'Confirmed',
    Planned = 'Planned',
    InProduction = 'InProduction',
    ReadyForDelivery = 'ReadyForDelivery',
}

export enum CustomerOrderPositionStatus {
    Draft = 'Draft',
    Valid = 'Valid',
    Confirmed = 'Confirmed',
    Planned = 'Planned',
    InProduction = 'InProduction',
    ReadyForDelivery = 'ReadyForDelivery',
    Delivered = 'Delivered',
    Invoiced = 'Invoiced',
    ActionRequired = 'ActionRequired',
    Cancelled = 'Cancelled',
}

export enum Priority {
    Low = 'Low',
    Normal = 'Normal',
    High = 'High',
    Urgent = 'Urgent',
}

export interface ICustomerOrderPositionRequestDto {
    positionNumber: null | number;
    articleId: string;
    targetQuantity: number;
    price: number;
    vat: number;
    discount: number;
    expressSurcharge: number;
    targetDeliveryDate: string;
    commissionNumber: null | string;
}

/** OData query for position grids that need article display fields (incl. quantity unit). */
export const CUSTOMER_ORDER_POSITIONS_LIST_QUERY = '?$expand=article($select=id,name,itemNo,type;$expand=partDetails($select=quantityUnit))';

export interface ICustomerOrderPositionArticle {
    id: string;
    name: string;
    itemNo?: string | null;
}

export interface ICustomerOrderPositionPricing {
    price: number;
    vat: number;
    discount: number;
    expressSurcharge: number;
    netPricePerPosition: number;
    grossPricePerPosition: number;
}

export interface ICustomerOrderPositionResponseDto extends ICustomerOrderPositionRequestDto {
    id: string;
    version: null | string;
    customerOrderId: string;
    netPricePerPosition: number;
    grossPricePerPosition: number;
    pricing?: ICustomerOrderPositionPricing | null;
    drawingImportId?: string | null;
    currentDeliveredQuantity: number;
    completeReadyForDeliveryOn?: string | null;
    status?: string;
    stateFlags?: OrderStatus[] | null;
    article?: IArticleResponse | null;
}

export interface ICustomerOrderPositionFormData extends ICustomerOrderPositionRequestDto {
    articleName: string | null;
    article: IArticleResponse | null;
    itemNo?: string | null;
    itemNoExternal?: string | null;
    customerId?: string | null;
    drawingFileId: string | null;
    previewImageId: string | null;
    rawMaterialId: string | null;
    productionSteps: string[];
    importedArticleType: ImportedArticleType;
    /** Position discount in percent (maps to request `discount`). */
    discount: number;
    /** Position surcharge in percent (maps to request `expressSurcharge`). */
    positionSurcharge: number;
}

export interface IOrderPatchRequest {
    status?: OrderStatus | null;
}

export enum CustomerOrderPositionReorderDirection {
    Up = 'Up',
    Down = 'Down',
}

export interface IPatchCustomerOrderPositionRequestDto {
    direction: CustomerOrderPositionReorderDirection;
}
